import { FC, ReactNode, useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { IntlConfig } from "react-intl/src/types";

import OverlayLoading from "src/components/DataStates/OverlayLoading.tsx";
import PageError from "src/components/Errors/PageError.tsx";
import {
  CustomIntlProvider,
  LocaleKeysRecord,
} from "src/internationalization/CustomIntlProvider.tsx";
import { SupportedLocale } from "src/internationalization/locale";
import { useLocale } from "src/internationalization/locale/useLocale.ts";

const loadMessages = async (locale: SupportedLocale): Promise<LocaleKeysRecord> => {
  switch (locale) {
    case "en-GB":
      return (await import("./texts/en-GB.ts")).default;
    default:
      return (await import("./texts/fr-FR.ts")).default;
  }
};

export const InternationalizationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { locale } = useLocale();
  const [messages, setMessages] = useState<IntlConfig["messages"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setLoading(true);
    loadMessages(locale)
      .then((data) => {
        setMessages(data);
        setTimeout(() => {
          setLoading(false);
        }, 500);
      })
      .catch(() => setHasError(true));
  }, [locale]);

  if (loading) {
    return <OverlayLoading />;
  }

  if (hasError) {
    return (
      <PageError title="Error while loading texts" description="Try again" textButton="Reload" />
    );
  }

  return messages !== null ? (
    <IntlProvider locale={locale} messages={messages}>
      <CustomIntlProvider>{children}</CustomIntlProvider>
    </IntlProvider>
  ) : null;
};
